import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { canViewHR, fmtDate } from "@/lib/helpers";
import {
  PageHeader, BtnPrimary, BtnSecondary, BtnGhost, Empty, Field, Input, Select,
} from "@/components/primitives";
import { toast } from "sonner";
import {
  CalendarBlank, FloppyDisk, Trash, ArrowClockwise, UploadSimple, Confetti,
  Rows, GridFour,
} from "@phosphor-icons/react";

const SHIFT_TYPES = [
  { value: "day", label: "Day" },
  { value: "night", label: "Night" },
  { value: "off", label: "Off" },
  { value: "leave", label: "Leave" },
  { value: "holiday", label: "Public Holiday" },
];

/**
 * Calendar view for hours entry. Renders each cycle day as a tile in a 7-col
 * grid (offset so weekdays align). Each tile shows hours + OT with inline
 * inputs — auto-saves on blur / Enter. Holidays get an amber tint + label;
 * off-day cells get a violet tint. The same `entries` map + save handlers
 * that power the list view are used so payslips and payroll formulas stay
 * in sync automatically.
 */
function HoursCalendar({ dates, entries, selectedEmp, holidays, defaultHours, setEntry, saveEntry }) {
  if (!dates.length) return null;
  // Prepend Sunday-based leading blanks so the first date lands under its DOW column
  const firstDow = new Date(dates[0]).getDay(); // 0 = Sunday
  const leadingBlanks = Array.from({ length: firstDow }, (_, i) => `blank-${i}`);
  const cells = [...leadingBlanks, ...dates];
  const dowLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="p-4">
      <div className="grid grid-cols-7 gap-1.5 mb-2">
        {dowLabels.map((d) => (
          <div key={d} className="text-[10px] uppercase tracking-wider text-[#a1a1aa] font-semibold text-center pb-1">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1.5" data-testid="dh-calendar-grid">
        {cells.map((c) => {
          if (typeof c === "string" && c.startsWith("blank-")) {
            return <div key={c} className="rounded-md border border-dashed border-[#f4f4f5] min-h-[92px]" />;
          }
          const d = c;
          const k = `${d}|${selectedEmp}`;
          const e = entries[k] || { hours: 0, overtime_hours: 0, off_day: false, shift_type: "day" };
          const dateObj = new Date(d);
          const dayNum = dateObj.getDate();
          const isHol = !!holidays[d];
          const holName = holidays[d] || "";
          const isOff = !!e.off_day;
          const isEmpty = !e.hours && !e.overtime_hours;
          const bg = isHol ? "bg-[#FFF6E5] border-[#F5C36F]"
                   : isOff ? "bg-[#F5F0FF] border-[#C7B8F5]"
                   : isEmpty ? "bg-white border-[#e4e4e7]"
                   : "bg-[#F0F4FF] border-[#B7C6FF]";
          return (
            <div
              key={d}
              data-testid={`dh-cell-${d}`}
              className={`relative rounded-md border ${bg} p-1.5 min-h-[92px] flex flex-col text-[11px]`}
            >
              <div className="flex items-start justify-between">
                <span className={`font-bold text-xs ${isHol ? "text-[#B25000]" : "text-[#27272a]"}`}>{dayNum}</span>
                <div className="flex flex-col items-end gap-0.5">
                  {isHol && <span className="text-[9px] font-bold text-[#B25000] uppercase" title={holName}>H</span>}
                  <label className="flex items-center gap-1 cursor-pointer" title="Off-day worked (2×)">
                    <input
                      type="checkbox"
                      checked={isOff}
                      onChange={(ev) => setEntry(d, { off_day: ev.target.checked })}
                      onBlur={() => saveEntry(d)}
                      className="h-3 w-3 accent-[#FF6A00]"
                      data-testid={`dh-cal-off-${d}`}
                    />
                    <span className="text-[9px] text-[#71717a] uppercase">off</span>
                  </label>
                </div>
              </div>
              <div className="mt-auto space-y-1">
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    step="0.25"
                    min="0"
                    value={e.hours || ""}
                    placeholder={String(defaultHours)}
                    onChange={(ev) => setEntry(d, { hours: ev.target.value })}
                    onBlur={() => saveEntry(d)}
                    onKeyDown={(ev) => { if (ev.key === "Enter") ev.target.blur(); }}
                    className="mono text-center w-full text-sm font-bold text-[#002FA7] bg-transparent border border-[#e4e4e7] rounded px-1 py-0.5 focus:border-[#002FA7] focus:outline-none"
                    data-testid={`dh-cal-hrs-${d}`}
                    title="Normal hours"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    step="0.25"
                    min="0"
                    value={e.overtime_hours || ""}
                    placeholder="OT"
                    onChange={(ev) => setEntry(d, { overtime_hours: ev.target.value })}
                    onBlur={() => saveEntry(d)}
                    onKeyDown={(ev) => { if (ev.key === "Enter") ev.target.blur(); }}
                    className="mono text-center w-full text-[10px] font-semibold text-[#FF6A00] bg-transparent border border-[#f4f4f5] rounded px-1 py-0.5 focus:border-[#FF6A00] focus:outline-none"
                    data-testid={`dh-cal-ot-${d}`}
                    title="Explicit overtime (2×)"
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 phizo-label text-[10px] flex flex-wrap gap-3">
        <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-[#FFF6E5] border border-[#F5C36F]" /> Public Holiday (2×)</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-[#F5F0FF] border border-[#C7B8F5]" /> Off-Day (2×)</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-[#F0F4FF] border border-[#B7C6FF]" /> Hours logged</span>
        <span className="ml-auto">Tap a cell · type hours · press Enter or click away to auto-save · switches propagate to payslips instantly</span>
      </div>
    </div>
  );
}

function currentCycle() {
  // Cycle closes 26th, begins 27th. If today ≥ 27th, cycle is [27th this month → 26th next month]
  const now = new Date();
  const day = now.getDate();
  const y = now.getFullYear();
  const m = now.getMonth();
  let start, end;
  if (day >= 27) {
    start = new Date(y, m, 27);
    end = new Date(y, m + 1, 26);
  } else {
    start = new Date(y, m - 1, 27);
    end = new Date(y, m, 26);
  }
  const iso = (d) => d.toISOString().slice(0, 10);
  return { start: iso(start), end: iso(end) };
}

function eachDate(startIso, endIso) {
  const out = [];
  const s = new Date(startIso), e = new Date(endIso);
  for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

export default function DailyHours() {
  const { user } = useAuth();
  const canEdit = canViewHR(user.role);
  const [employees, setEmployees] = useState([]);
  const [rosters, setRosters] = useState([]);
  const [entries, setEntries] = useState({}); // `${date}|${code}` → row
  const [holidays, setHolidays] = useState({}); // date → name
  const [selectedEmp, setSelectedEmp] = useState("");
  const [cycle, setCycle] = useState(currentCycle());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [viewMode, setViewMode] = useState("calendar"); // "calendar" | "list"
  const fileInput = useRef(null);

  async function loadAll() {
    setLoading(true);
    try {
      const [emps, rs, hrs, hol] = await Promise.all([
        api.get("/employees?status=active"),
        api.get("/rosters"),
        api.get(`/daily-hours?start=${cycle.start}&end=${cycle.end}`),
        api.get(`/public-holidays?year=${new Date(cycle.start).getFullYear()}`),
      ]);
      setEmployees(emps.data);
      setRosters(rs.data);
      const map = {};
      for (const h of hrs.data) map[`${h.date}|${h.employee_code}`] = h;
      setEntries(map);
      const hmap = {};
      for (const h of hol.data) hmap[h.date] = h.name;
      // Also load the next year in case the cycle spans a year boundary
      const nextY = new Date(cycle.end).getFullYear();
      if (nextY !== new Date(cycle.start).getFullYear()) {
        try {
          const { data } = await api.get(`/public-holidays?year=${nextY}`);
          for (const h of data) hmap[h.date] = h.name;
        } catch (_e) { /* non-fatal */ }
      }
      setHolidays(hmap);
      if (!selectedEmp && emps.data.length) setSelectedEmp(emps.data[0].employee_code);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { loadAll(); }, [cycle.start, cycle.end]);

  const dates = useMemo(() => eachDate(cycle.start, cycle.end), [cycle.start, cycle.end]);
  const activeEmp = employees.find((e) => e.employee_code === selectedEmp);
  const roster = rosters.find((r) => r.id === activeEmp?.roster_id);
  const defaultHours = roster?.hours_per_day || 8;

  function setEntry(date, patch) {
    const k = `${date}|${selectedEmp}`;
    const prev = entries[k] || { date, employee_code: selectedEmp, hours: 0, overtime_hours: 0, off_day: false, shift_type: "day" };
    setEntries({ ...entries, [k]: { ...prev, ...patch } });
  }

  async function saveEntry(date) {
    const k = `${date}|${selectedEmp}`;
    const e = entries[k];
    if (!e) return;
    setSaving(true);
    try {
      const payload = {
        date, employee_code: selectedEmp,
        employee_name: activeEmp?.full_name,
        hours: Number(e.hours || 0),
        overtime_hours: Number(e.overtime_hours || 0),
        off_day: !!e.off_day,
        shift_type: e.shift_type || "day",
        notes: e.notes || "",
      };
      const { data } = await api.post("/daily-hours", payload);
      setEntries((cur) => ({ ...cur, [k]: data }));
      toast.success(`${date} saved`);
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  }

  async function saveAll() {
    if (!selectedEmp) return;
    setSaving(true);
    try {
      const rows = dates.map((d) => {
        const k = `${d}|${selectedEmp}`;
        const e = entries[k];
        if (!e) return null;
        return {
          date: d, employee_code: selectedEmp,
          employee_name: activeEmp?.full_name,
          hours: Number(e.hours || 0),
          overtime_hours: Number(e.overtime_hours || 0),
          off_day: !!e.off_day,
          shift_type: e.shift_type || "day",
          notes: e.notes || "",
        };
      }).filter(Boolean);
      if (!rows.length) { toast.error("Nothing to save"); return; }
      await api.post("/daily-hours/bulk", { rows });
      toast.success(`Saved ${rows.length} entries`);
      loadAll();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  }

  function autoFillFromRoster() {
    if (!roster) return toast.error("Employee has no roster assigned");
    const draft = { ...entries };
    for (const d of dates) {
      const day = new Date(d).getDay(); // 0 = Sunday
      const isWeekend = day === 0 || day === 6;
      const isHoliday = !!holidays[d];
      const k = `${d}|${selectedEmp}`;
      draft[k] = {
        ...(draft[k] || {}),
        date: d,
        employee_code: selectedEmp,
        hours: (isWeekend || isHoliday) ? 0 : defaultHours,
        overtime_hours: draft[k]?.overtime_hours || 0,
        off_day: isWeekend, // pre-tick so weekend work is auto 2×
        shift_type: isHoliday ? "holiday" : (isWeekend ? "off" : "day"),
      };
    }
    setEntries(draft);
    toast.success(`Auto-filled ${dates.length} days from ${roster.name}`);
  }

  async function removeEntry(date) {
    const k = `${date}|${selectedEmp}`;
    const e = entries[k];
    if (!e?.id) {
      const { [k]: _drop, ...rest } = entries; setEntries(rest); return;
    }
    try {
      await api.delete(`/daily-hours/${e.id}`);
      const { [k]: _drop, ...rest } = entries;
      setEntries(rest);
      toast.success(`${date} cleared`);
    } catch (err) { toast.error(formatError(err)); }
  }

  async function importExcel(file) {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setImporting(true);
    try {
      const { data } = await api.post("/daily-hours/import-excel", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.inserted} new + ${data.updated} updated (${data.skipped} skipped)`);
      loadAll();
    } catch (err) { toast.error(formatError(err)); }
    finally { setImporting(false); if (fileInput.current) fileInput.current.value = ""; }
  }

  const totalNormal = dates.reduce((s, d) => s + Number(entries[`${d}|${selectedEmp}`]?.hours || 0), 0);
  const totalOT = dates.reduce((s, d) => {
    const e = entries[`${d}|${selectedEmp}`];
    if (!e) return s;
    const explicit = Number(e.overtime_hours || 0);
    const dayOT = (holidays[d] || e.off_day) ? Number(e.hours || 0) : 0;
    return s + explicit + dayOT;
  }, 0);
  const totalNormalEffective = dates.reduce((s, d) => {
    const e = entries[`${d}|${selectedEmp}`];
    if (!e) return s;
    if (holidays[d] || e.off_day) return s;
    return s + Number(e.hours || 0);
  }, 0);

  if (!canEdit) {
    return <Empty label="Only HR / Admin can access daily hours." />;
  }

  return (
    <div data-testid="daily-hours-page">
      <PageHeader
        overline="Module / HR"
        title="Daily Hours"
        subtitle="Log hours per employee per day. Aggregated automatically on the 26th when payroll is run."
        actions={
          <>
            <input
              ref={fileInput}
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={(e) => importExcel(e.target.files?.[0])}
              data-testid="dh-import-input"
            />
            <BtnSecondary onClick={() => fileInput.current?.click()} disabled={importing} data-testid="dh-import-btn">
              <UploadSimple size={14} weight="bold" className="inline mr-1.5" />
              {importing ? "Importing…" : "Import Excel"}
            </BtnSecondary>
            <BtnSecondary onClick={autoFillFromRoster} data-testid="dh-autofill">
              <ArrowClockwise size={14} weight="bold" className="inline mr-1.5" />
              Auto-fill from Roster
            </BtnSecondary>
            <BtnPrimary onClick={saveAll} disabled={saving} data-testid="dh-save-all">
              <FloppyDisk size={14} weight="bold" className="inline mr-1.5" />
              {saving ? "Saving…" : "Save All"}
            </BtnPrimary>
          </>
        }
      />

      <div className="phizo-card p-4 mb-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
        <Field label="Employee">
          <Select value={selectedEmp} onChange={(e) => setSelectedEmp(e.target.value)}
            data-testid="dh-employee">
            {employees.map((emp) => (
              <option key={emp.employee_code} value={emp.employee_code}>
                {`${emp.employee_code} · ${emp.full_name}`}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Cycle Start (27th)">
          <Input type="date" value={cycle.start} data-testid="dh-start"
            onChange={(e) => setCycle({ ...cycle, start: e.target.value })} />
        </Field>
        <Field label="Cycle End (26th)">
          <Input type="date" value={cycle.end} data-testid="dh-end"
            onChange={(e) => setCycle({ ...cycle, end: e.target.value })} />
        </Field>
        <Field label="Roster · Hrs/Day">
          <Input value={roster ? `${roster.name} · ${defaultHours} hrs` : "— no roster —"} readOnly />
        </Field>
      </div>

      {/* View mode toggle — Calendar vs List */}
      <div className="flex items-center gap-2 mb-3 phizo-label">
        <span className="uppercase tracking-wider text-xs">View:</span>
        <button
          type="button"
          onClick={() => setViewMode("calendar")}
          className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 border ${viewMode === "calendar" ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-[#3f3f46] border-[#e4e4e7] hover:border-[#a1a1aa]"}`}
          data-testid="dh-view-calendar"
        >
          <GridFour size={13} weight="bold" /> Calendar
        </button>
        <button
          type="button"
          onClick={() => setViewMode("list")}
          className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 border ${viewMode === "list" ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-[#3f3f46] border-[#e4e4e7] hover:border-[#a1a1aa]"}`}
          data-testid="dh-view-list"
        >
          <Rows size={13} weight="bold" /> List
        </button>
      </div>

      <div className="phizo-card">
        <div className="p-3 border-b border-[#e4e4e7] flex flex-wrap items-center justify-between gap-3">
          <div className="phizo-label flex items-center gap-2">
            <CalendarBlank size={14} weight="bold" />
            {activeEmp?.full_name || "—"} · {dates.length} days
          </div>
          <div className="flex gap-3 phizo-label" data-testid="dh-totals">
            <span>Normal: <span className="mono text-[#002FA7]">{totalNormalEffective.toFixed(1)}h</span></span>
            <span>OT (2×): <span className="mono text-[#FF6A00]">{totalOT.toFixed(1)}h</span></span>
            <span>Raw total: <span className="mono">{totalNormal.toFixed(1)}h</span></span>
          </div>
        </div>
        {loading ? (
          <div className="p-5 phizo-label">Loading…</div>
        ) : viewMode === "calendar" ? (
          <HoursCalendar
            dates={dates}
            entries={entries}
            selectedEmp={selectedEmp}
            holidays={holidays}
            defaultHours={defaultHours}
            setEntry={setEntry}
            saveEntry={saveEntry}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="phizo-table">
              <thead>
                <tr>
                  <th>Date</th><th>Day</th><th>Shift</th>
                  <th className="text-right">Hours</th>
                  <th className="text-right">OT (hrs)</th>
                  <th className="text-center">Off Day</th>
                  <th>Notes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {dates.map((d) => {
                  const k = `${d}|${selectedEmp}`;
                  const e = entries[k] || { hours: 0, overtime_hours: 0, off_day: false, shift_type: "day" };
                  const dow = new Date(d).toLocaleDateString("en-GB", { weekday: "short" });
                  const isHol = !!holidays[d];
                  const rowClass = isHol ? "bg-[#FFF6E5]" : (e.off_day ? "bg-[#F5F0FF]" : "");
                  return (
                    <tr key={d} data-testid={`dh-row-${d}`} className={rowClass}>
                      <td className="mono text-sm whitespace-nowrap">
                        {fmtDate(d)}
                        {isHol && (
                          <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold text-[#B25000]" title={holidays[d]}>
                            <Confetti size={11} weight="bold" />
                            HOLIDAY
                          </span>
                        )}
                      </td>
                      <td className="text-xs uppercase text-[#a1a1aa]">{dow}</td>
                      <td>
                        <Select value={e.shift_type || "day"}
                          onChange={(ev) => setEntry(d, { shift_type: ev.target.value })}
                          className="w-28"
                          data-testid={`dh-shift-${d}`}>
                          {SHIFT_TYPES.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </Select>
                      </td>
                      <td className="text-right">
                        <Input type="number" step="0.25" min="0" value={e.hours || 0}
                          onChange={(ev) => setEntry(d, { hours: ev.target.value })}
                          className="text-right mono w-20"
                          data-testid={`dh-hours-${d}`} />
                      </td>
                      <td className="text-right">
                        <Input type="number" step="0.25" min="0" value={e.overtime_hours || 0}
                          onChange={(ev) => setEntry(d, { overtime_hours: ev.target.value })}
                          className="text-right mono w-20"
                          data-testid={`dh-ot-${d}`} />
                      </td>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={!!e.off_day}
                          onChange={(ev) => setEntry(d, { off_day: ev.target.checked })}
                          data-testid={`dh-offday-${d}`}
                          title="Tick if the employee worked on a scheduled off day — all hours become 2× overtime"
                          className="h-4 w-4 accent-[#FF6A00]"
                        />
                      </td>
                      <td>
                        <Input value={e.notes || ""}
                          onChange={(ev) => setEntry(d, { notes: ev.target.value })}
                          className="text-xs" placeholder="—" />
                      </td>
                      <td className="text-right whitespace-nowrap">
                        <BtnGhost onClick={() => saveEntry(d)} data-testid={`dh-save-${d}`}>
                          <FloppyDisk size={12} weight="bold" />
                        </BtnGhost>
                        {e.id && (
                          <BtnGhost onClick={() => removeEntry(d)} className="text-[#FF2A2A]">
                            <Trash size={12} weight="bold" />
                          </BtnGhost>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="p-3 border-t border-[#e4e4e7] phizo-label text-xs">
              Tip · Upload an Excel with columns: <span className="mono">Date · Employee Code · Hours · Overtime Hours · Off Day · Notes</span>. Any date matching a Zambian public holiday, or with the <em>Off Day</em> box ticked, will be paid at 2× the normal rate on payroll.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
